import { Fragment, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { DraftDesignConfiguration, MeasureValue, ProductType, ProductTypeSize } from '@/types';
import { t } from '@/utils/i18n';
import { getImgBaseUrl } from '@/utils/imageUrls/getImgBaseUrl';
import { MM_PER_INCH } from '@/utils/math/consts';
import { isInches } from '@/utils/params';
import { STICKER_SIZE_ROUTE } from '@/hooks/useStickerSize';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { isOneSizeProductType } from '@/model/productType/isOneSizeProductType';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { StoreState } from '@/redux/types';
import { setSelectedConfigurationAction } from '../draft/draftState/actions/setSelectedConfigurationAction';
import { getLocalizedSize as getLocalizedSizeString } from '../draft/utils/getLocalizedSize';
import { ButtonLink } from './Button';
import { Info } from './icons/Info';
import { Resize } from './icons/Resize';
import { Ruler } from './icons/Ruler';
import './SizeTable.scss';

const SIZE = '200';

const getLocalizedSize = (value: MeasureValue) => {
  if (isInches()) {
    return (value.value / MM_PER_INCH).toFixed(2);
  }
  return (value.value / 10).toFixed(1);
};

interface SizeProps {
  size: ProductTypeSize;
}

const Size: FunctionComponent<SizeProps> = ({ size }) => (
  <tr>
    <th scope="row">{size.name}</th>
    {[...size.measures]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((m) => (
        <td key={m.name}>{getLocalizedSize(m.value)}</td>
      ))}
  </tr>
);

interface SizeTableProps {
  productType: ProductType;
}

export const SizeTable: FunctionComponent<SizeTableProps> = ({ productType }) => {
  const unit = isInches() ? 'in' : 'cm';
  const src = `${getImgBaseUrl()}/productTypes/${
    productType.id
  }/variants/size,width=${SIZE},height=${SIZE}.png`;
  const isUniSize = isOneSizeProductType(productType);
  const {
    sizes: [{ measures }],
  } = productType;

  const sortedMeasures = [...measures].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="size-table">
      <div className="size-table__measures">
        {!isUniSize && (
          <div className="size-table__hint">
            <Ruler width="1.5em" height="1.5em" className="size-table__hint-icon" />
            {t(`sizePanel.sizeFitHint.${productType.sizeFitHintId}`)}
          </div>
        )}
        <div className="size-table__image">
          <img src={src} alt="" width={SIZE} height={SIZE} />
        </div>
      </div>
      <div className="size-table__sizes">
        <table>
          <thead>
            <tr>
              <th scope="row">{t('sizePanel.title')}</th>
              {sortedMeasures.map((m) => (
                <th key={m.name}>
                  {m.name}
                  &nbsp;(
                  {unit})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {productType.sizes.map((s) => (
              <Size size={s} key={s.id} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const StickerSizeTable: FunctionComponent<SizeTableProps> = ({ productType }) => {
  const draftDispatch = useReduxDraftDispatch();
  const src = `${getImgBaseUrl()}/productTypes/${
    productType.id
  }/variants/size,width=${SIZE},height=${SIZE}.png`;

  const {
    sizes: [{ measures }],
  } = productType;

  const configuration = useSelector(
    (state: StoreState) => selectConfigurations(state)[0] as DraftDesignConfiguration
  );
  const designSize = getCurrentSizeOfConfiguration(configuration);
  const sortedMeasures = [...measures].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Fragment>
      <div className="size-table">
        <div className="size-table__measures">
          <div className="size-table__image">
            <img src={src} alt="" width={SIZE} height={SIZE} />
          </div>
        </div>
        <div className="size-table__sizes">
          <table>
            <thead>
              <tr>
                <th scope="row">{t('sizePanel.title')}</th>
                {sortedMeasures.map((m) => (
                  <th key={m.name}>{m.name}&nbsp;(mm)</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{`${getLocalizedSizeString(
                  designSize.height
                )} x ${getLocalizedSizeString(designSize.width)}`}</th>

                <td>{getLocalizedSizeString(designSize.height, false)}</td>
                <td>{getLocalizedSizeString(designSize.width, false)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="size-table__sticker-size-link">
          <ButtonLink
            to={STICKER_SIZE_ROUTE}
            light
            onClick={() => draftDispatch(setSelectedConfigurationAction(configuration))}
          >
            <Resize />
            {t('basket.chooseSize')}
          </ButtonLink>
        </div>
      </div>
      <div className="size-table__sticker-info">
        <Info width="2rem" heigth="2rem" className="size-table__sticker-info-icon" />
        <p>{t('sticker.basket.hint')}</p>
      </div>
    </Fragment>
  );
};
